<template>
    <Panel>
        <PanelHeader heading="Auction Settings" />
        <PanelBody>
            <Fieldset>
                <AccountsSelectCreate
                    label="Select a Seller (Optional)"
                    :auctioneer_id="vehicle_asset.auctioneer_id"
                    :enable_create="true"
                    :account_type="'sellers'"
                    :existing_account_id="vehicle_asset?.seller_account_id"
                    v-on:account_selected="account => sellerSelected(account)"
                />
                <div class="bg-blue-50 mb-2 p-2 rounded-md text-blue-500 flex mt-2">
                    <InformationCircleIcon class="flex-shrink-0 mr-1.5 h-5 w-5" aria-hidden="true" />
                    <p class="text-sm font-medium">You won't be able to add this vehicle to a sale until a seller has been assigned.</p>
                </div>
            </Fieldset>

            <Fieldset>
                <AssetAssignSaleSectionSaleCode
                    v-if="vehicle_asset"
                    :asset="vehicle_asset"
                    v-on:sale_section_code_updated="saleSectionCodeUpdated"
                />
            </Fieldset>

            <Fieldset>
                <FormField namespace="asset" name="master_opening_bid" :field="form.asset.fields.master_opening_bid" class="mb-4"/>
                <FormField namespace="asset" name="bid_interval" :field="form.asset.fields.bid_interval" class="mb-4"/>
                <FormField namespace="asset" name="master_guide_price_min" :field="form.asset.fields.master_guide_price_min" class="mb-4"/>
                <FormField namespace="asset" name="master_guide_price_max" :field="form.asset.fields.master_guide_price_max" class="mb-4"/>
                <FormField namespace="asset" name="public_display_guide_price" :field="form.asset.fields.public_display_guide_price" />
            </Fieldset>

            <Fieldset heading="Lot Number">

                <div v-if="form.lot.fields.lot_number.value" class="flex justify-between items-center">
                    <h3 class="font-medium text-md text-gray-700">{{ `Assign Lot No: ${form.lot.fields.lot_number.value}` }}</h3>
                    <Button width="w-auto" size="sm" color="blue-outline" :onClick="() => resetLotnoState()">Clear</Button>
                </div>

                <div v-if="!form.lot.fields.lot_number.value" class="mb-8">
                    <form action="#" @submit.prevent="checkLotNumberAvailability">
                        <div class="flex items-stretch">
                            <FormField :field="form.lot_number.fields.requested_lotno" namespace="lot_number" :name="'requested_lotno'" class="w-full" />
                            <Button type="submit" width="w-full" color="blue-100" class="ml-2" :disabled="form.lot_number.loading">
                                <span>{{ form.lot_number.loading ? 'Checking availability' : 'Check availability' }}</span>
                                <LoadingIcon v-if="form.lot_number.loading" class="ml-2 mr-3 h-5 w-5 animate-reverse-spin" />
                            </Button>
                        </div>
                    </form>
                    <Message :active="state.search_message_heading" :heading="state.search_message_heading" :body="state.search_message_body" :type="state.search_message_success ? 'success' : 'error'" class="mt-2" />
                </div>
                <div v-if="!form.lot.fields.lot_number.value && state.suggested_lotnos">
                    <h3 class="block text-sm font-medium text-gray-700 mb-2">Suggested Lot Numbers</h3>
                    <Button v-for="lotno in state.suggested_lotnos" :onClick="() => setLotNumber(lotno)" :key="lotno" width="w-auto" size="sm" class="mr-2 mb-2">{{ lotno }}</Button>
                </div>
            </Fieldset>

<!--            <Fieldset heading="Grade" subheading="Grade and notes">-->
<!--                <FormField namespace="asset" name="auctioneer_grade" :field="form.asset.fields.auctioneer_grade" class="mb-4" />-->
<!--                <FormField namespace="asset" name="auctioneer_grade_notes" :field="form.asset.fields.auctioneer_grade_notes" class="mb-4" />-->
<!--                <FormField namespace="asset" name="declared_faults" :field="form.asset.fields.declared_faults" />-->
<!--            </Fieldset>-->

            <Fieldset heading="Public details" subheading="The title and description that will be published on the website">
                <FormField namespace="asset" name="public_headline" :field="form.asset.fields.public_headline" class="mb-4"/>
                <FormField namespace="asset" name="public_body" :field="form.asset.fields.public_body" class="mb-4" />
                <FormField namespace="asset" name="auctioneer_quote" :field="form.asset.fields.auctioneer_quote" />
            </Fieldset>

            <AssetInsuranceChecks
                v-if="vehicle_asset_id"
                :asset_id="vehicle_asset_id"
                :allow_create="true"
                :view_types="['vehicle:manual_check']"
                :create_types="['vehicle:manual_check']"
            />

        </PanelBody>
        <PanelFooter>
            <Button :onClick="() => VEHICLE_STORE.activatePrevStep()" width="w-auto" type="button" color="blue-outline">Back</Button>
            <Button :onClick="() => saveStep()" width="w-auto" type="button" class="ml-2">Save</Button>
        </PanelFooter>
    </Panel>
</template>
<script>
import { reactive, computed, watch, onMounted, onBeforeMount, ref } from 'vue'
import { getPayload, isValid, penceToPounds, poundsToPence } from '@lib/helpers'
import moment from 'moment'
import * as SaleService from '@ser/sale'
import { useVehicleStore } from '@sto/vehicle.store'
import { useAssetStore } from '@sto/asset.store'
import { useSystemStore } from '@sto/system.store'

import AssetAssignSaleSectionSaleCode from "@par/asset/AssetAssignSaleSectionSaleCode.vue"
import FormField from "@glo/FormField.vue";
import Fieldset from "@glo/Fieldset.vue";
import AccountsSelectCreate from "@par/account/AccountsSelectCreate.vue";
import InformationCircleIcon from "@ico/InformationCircleIcon.vue";
import Button from "@glo/Button.vue";
import LoadingIcon from "@ico/LoadingIcon.vue";
import AssetInsuranceChecks from "@par/asset/AssetInsuranceChecks.vue"

export default {
    components: {
        LoadingIcon,
        Button,
        InformationCircleIcon,
        AccountsSelectCreate,
        Fieldset,
        FormField,
        AssetAssignSaleSectionSaleCode,
        AssetInsuranceChecks
    },
    setup(props) {

        const VEHICLE_STORE = useVehicleStore()
        const ASSET_STORE = useAssetStore()
        const SYSTEM_STORE = useSystemStore()

        const asset_description_modal = ref(null)

        const vehicle_id = computed(() => {
            return VEHICLE_STORE?.vehicle?.id || null
        })

        const vehicle = computed(() => {
            return VEHICLE_STORE?.vehicle || null
        })

        const vehicle_asset = computed(() => {
            return VEHICLE_STORE?.vehicle?.asset || null
        })

        const vehicle_asset_id = computed(() => {
            return VEHICLE_STORE?.vehicle?.asset?.id || null
        })

        const state = reactive({
            sale_code_update_loading: false,
            search_message: null,
            search_message_success: false,
            search_message_heading: null,
            search_message_body: null,
            lotno_search_timeout: null,
            suggested_lotnos: []
        })

        const form = reactive({
            loading: false,
            // the sale code assignnment form
            sale_settings: {
                fields: {
                    sale_code_id: {
                        active: true,
                        label: 'Sale code',
                        type: 'hidden',
                        value: null,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    sale_section_id: {
                        active: true,
                        label: 'Sale code',
                        type: 'hidden',
                        value: null,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                }
            },
            asset: {
                fields: {
                    master_opening_bid: {
                        active: true,
                        value: vehicle_asset.value?.master_opening_bid
                            ? penceToPounds(vehicle_asset.value?.master_opening_bid)
                            : 0,
                        label: 'Opening bid',
                        type: 'number',
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    bid_interval: {
                        active: true,
                        value: vehicle_asset.value?.bid_interval
                            ? penceToPounds(vehicle_asset.value?.bid_interval)
                            : 0,
                        label: 'Bid interval',
                        type: 'number',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [
                            { type: 'min', value: 1 }
                        ],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    master_guide_price_min: {
                        active: true,
                        value: vehicle_asset.value?.master_guide_price_min
                            ? penceToPounds(vehicle_asset.value?.master_guide_price_min)
                            : 0,
                        label: 'NET Guide Price Minimum',
                        type: 'number',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    master_guide_price_max: {
                        active: true,
                        value: vehicle_asset.value?.master_guide_price_max
                            ? penceToPounds(vehicle_asset.value?.master_guide_price_max)
                            : 0,
                        label: 'NET Guide Price Maximum',
                        type: 'number',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    public_display_guide_price: {
                        active: true,
                        label: 'Display guide price to public',
                        type: 'toggle',
                        value: vehicle_asset.value?.public_display_guide_price === 0
                            ? false
                            : true,
                        stored_value: true,
                        required: false,
                        pristine: true,
                        valid: true,
                        hide_hint: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    public_headline: {
                        active: true,
                        value: vehicle_asset.value?.public_headline || '',
                        label: 'Public headline',
                        type: 'text',
                        required: false,
                        pristine: true,
                        valid: true,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    public_body: {
                        active: true,
                        value: vehicle_asset.value?.public_body || '',
                        label: 'Public description',
                        type: 'richtext',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        // custom_height: '20rem',
                        ai_suggestions_enabled: false,
                        ai_suggestions_label: 'Get Suggestions',
                        // ai_generate_enabled: false,
                        // ai_generate_label: 'Generate with AI',
                        // ai_generate_loading: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    auctioneer_quote: {
                        active: true,
                        value: vehicle_asset.value?.auctioneer_quote || '',
                        label: 'Auctioneer quote',
                        type: 'richtext',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        required: false,
                        pristine: true,
                        valid: true,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    seller_id: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        validation: [],
                        valid: true
                    },
                    seller_account_id: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        validation: [],
                        valid: true
                    },
                    auctioneer_custody_from: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        validation: [],
                        valid: true
                    },
                    status: {
                        active: true,
                        type: 'hidden',
                        value: 'ready',
                        validation: [],
                        valid: true
                    },
                    auctioneer_grade: {
                        active: false,
                        label: 'Grade',
                        type: 'select',
                        value: vehicle_asset.value?.auctioneer_grade || '',
                        stored_value: vehicle_asset.value?.auctioneer_grade || '',
                        options: [
                            {
                                value: '',
                                label: 'Please choose...'
                            },
                            {
                                value: '',
                                label: 'Please choose...'
                            },
                            {
                                value: '1',
                                label: '1'
                            },
                            {
                                value: '1.5',
                                label: '1.5'
                            },
                            {
                                value: '2',
                                label: '2'
                            },
                            {
                                value: '2.5',
                                label: '2.5'
                            },
                            {
                                value: '3',
                                label: '3'
                            },
                            {
                                value: '3.5',
                                label: '3.5'
                            },
                            {
                                value: '4',
                                label: '4'
                            },
                            {
                                value: '4.5',
                                label: '4.5'
                            },
                            {
                                value: '5',
                                label: '5'
                            },
                            {
                                value: '1 (Trade)',
                                label: '1 (Trade)'
                            },
                            {
                                value: '1.5 (Trade)',
                                label: '1.5 (Trade)'
                            },
                            {
                                value: '2 (Trade)',
                                label: '2 (Trade)'
                            },
                            {
                                value: '2.5 (Trade)',
                                label: '2.5 (Trade)'
                            },
                            {
                                value: '3 (Trade)',
                                label: '3 (Trade)'
                            },
                            {
                                value: '3.5 (Trade)',
                                label: '3.5 (Trade)'
                            },
                            {
                                value: '4 (Trade)',
                                label: '4 (Trade)'
                            },
                            {
                                value: '4.5 (Trade)',
                                label: '4.5 (Trade)'
                            },
                            {
                                value: '5 (Trade)',
                                label: '5 (Trade)'
                            },
                            {
                                value: 'Classic',
                                label: 'Classic'
                            },
                            {
                                value: 'Unclassified',
                                label: 'Unclassified'
                            }
                        ],
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    auctioneer_grade_notes: {
                        active: false,
                        label: 'Grade notes',
                        type: 'richtext',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        value: vehicle_asset.value?.auctioneer_grade_notes || '',
                        stored_value: vehicle_asset.value?.auctioneer_grade_notes || '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    declared_faults: {
                        active: false,
                        label: 'Declared faults',
                        type: 'richtext',
                        richtext_options: {
                            formatting: ['bold', 'italic', 'ul']
                        },
                        value: vehicle_asset.value?.declared_faults || '',
                        stored_value: vehicle_asset.value?.declared_faults || '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                }
            },
            lot: {
                fields: {
                    lot_number: {
                        active: true,
                        label: 'Lot number',
                        type: 'hidden',
                        value: null,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    auctioneer_id: {
                        active: true,
                        label: '',
                        type: 'hidden',
                        value: null,
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    }
                }
            },
            lot_number: {
                loading: false,
                fields: {
                    requested_lotno: {
                        active: true,
                        label: null,
                        type: 'number',
                        placeholder: 'Lot number',
                        value: '',
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                    }
                }
            }
        })

        const setSearchMessage = (success, heading, body) => {
            state.search_message_success = success || false
            state.search_message_heading = heading || null
            state.search_message_body = body || null
        }

        const saleSectionCodeUpdated = data => {
            form.sale_settings.fields.sale_code_id.value = data?.sale_code_id || null
            form.sale_settings.fields.sale_section_id.value = data?.sale_section_id || null
        }

        const saveAsset = async payload => {
            return ASSET_STORE.update(vehicle_asset.value?.id, payload)
        }

        const saveLotNumber = async payload => {
            return ASSET_STORE.assignLotNumber(vehicle_asset.value?.id, payload)
        }

        const saveSaleSectionCode = async payload => {
            return ASSET_STORE.assignSaleCode(vehicle_asset.value?.auctioneer_id, vehicle_asset.value?.id, payload)
        }

        const saveStep = async () => {

            if (form.loading) return

            // make sure everything is valid
            if (!isValid(form.sale_settings.fields) || !isValid(form.asset.fields) || !isValid(form.lot.fields)) return

            try {

                form.loading = true

                let promises = []

                if (form.sale_settings.fields.sale_section_id.value && form.sale_settings.fields.sale_code_id.value) {
                    const sale_payload = getPayload(form.sale_settings.fields)
                    promises.push(saveSaleSectionCode(sale_payload))
                }

                const asset_payload = getPayload(form.asset.fields)
                promises.push(saveAsset(asset_payload))

                if (!form.lot.fields.auctioneer_id.value) form.lot.fields.auctioneer_id.value = vehicle_asset.value?.auctioneer_id
                if (form.lot.fields.lot_number.value && form.lot.fields.auctioneer_id.value) {
                    const lot_payload = getPayload(form.lot.fields)
                    promises.push(saveLotNumber(lot_payload))
                }

                await Promise.all(promises)

            } catch (error) {
                console.error('save error', error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was a problem saving some of your details')

            } finally {
                form.loading = false
                VEHICLE_STORE.activateNextStep()
            }


        }

        const getSuggestedLotNos = async () => {

            const params = []
            params.push({key: 'auctioneer_id', value: vehicle_asset.value?.auctioneer_id})
            const available = await SaleService.lotNumberAvailable(params)

            state.suggested_lotnos = available.suggested_lot_numbers
        }

        const sellerSelected = account => {
            form.asset.fields.seller_id.value = account?.seller?.id || null
            form.asset.fields.seller_account_id.value = account?.id || null
        }

        const setCustodyDate = () => {
            form.asset.fields.auctioneer_custody_from.value = moment().format('YYYY-MM-DD')
        }

        const checkLotNumberAvailability = async () => {

            if (form.lot_number.loading || !form.lot_number.fields.requested_lotno.value) return

            try {
                form.lot.fields.lot_number.value = null
                setSearchMessage(false, null, null)

                form.lot_number.loading = true

                let params = []
                params.push({key: 'auctioneer_id', value: vehicle_asset.value?.auctioneer_id })
                if (form.lot_number.fields.requested_lotno.value) params.push({key: 'lot_number', value: form.lot_number.fields.requested_lotno.value})
                const available = await SaleService.lotNumberAvailable(params)

                state.suggested_lotnos = Array.isArray(available?.suggested_lot_numbers) ? available?.suggested_lot_numbers : state.suggested_lotnos

                if (available.is_available === false) {
                    setSearchMessage(false, 'Not available', `Lot number ${available.requested_lot_number} in use`)
                    form.lot_number.loading = false
                    return
                } else {
                    setSearchMessage(true, 'Available', `Lot number ${available.requested_lot_number} is available`)
                    form.lot_number.loading = false
                }

                if (!available.requested_lot_number) {
                    setSearchMessage(false, 'Not available', `Looks like something went wrong!`)
                    form.lot_number.loading = false
                    return
                }

                setLotNumber(available.requested_lot_number)

            } catch (error) {

                console.error('SaleService.lotNumberAvailable error', error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was a problem checking availability on that lont number')

            } finally {
                form.lot_number.loading = false
            }
        }

        const setLotNumber = lot_number => {
            form.lot.fields.lot_number.value = lot_number
        }

        const resetLotnoState = () => {
            form.lot.fields.lot_number.value = null
            state.lotno_search_timeout = null
            form.lot_number.fields.requested_lotno.value = null
            state.search_message_body = null
            state.search_message_success = false
            state.search_message_heading = null
        }

        const generateDraftDescription = async () => {

            form.asset.fields.public_body.disabled = true
            form.asset.fields.public_body.ai_generate_loading = true

            try {
                const payload = {}
                const asset_description = await ASSET_STORE.generateDescription(vehicle_asset_id.value, payload)
                console.log('asset_description', asset_description)
                if (asset_description?.data?.transformed && typeof asset_description.data.transformed === 'string') {
                    form.asset.fields.public_body.value = asset_description.data.transformed
                    form.asset.fields.public_body.custom_height = "30rem"
                }

            } catch (error) {
                console.error('ASSET_STORE.generateDescription error', error)

            } finally {
                form.asset.fields.public_body.disabled = false
                form.asset.fields.public_body.ai_generate_loading = false
            }
        }

        const regeneratePublicBody = async () => {
            await generateDraftDescription()
        }

        const isClassicAsset = async (sale_section_id = null) => {

            if (!sale_section_id) return false

            try {
                const sale_section = await SaleService.getSaleSectionById(sale_section_id)
                if (typeof sale_section?.name !== 'string') return false
                // if sale_section.name includes the sub string "classic" return true
                return sale_section.name.toLowerCase().includes('classic')

            } catch (error) {
                console.error('Error fetching sale_section', error)
            }

            return false
        }

        const checkClassicStatus = async sale_section_id => {

            if (await isClassicAsset(sale_section_id)) {
                // define the UI for classic
                ASSET_STORE.setAssetId(vehicle_asset_id.value)
                form.asset.fields.public_body.ai_suggestions = (() => ASSET_STORE.openAssetCreateDrawer('asset_description_suggestions'))
                form.asset.fields.public_body.ai_suggestions_enabled = true

                // auto generate a description
                // if (!form.asset.fields.public_body.value) {
                //     await generateDraftDescription()
                // }

            } else {
                // define the UI for non-classic
                form.asset.fields.public_body.ai_suggestions = null
                form.asset.fields.public_body.ai_suggestions_enabled = false
            }
        }

        watch (
            () => form.sale_settings.fields.sale_section_id.value,
            async sale_section_id => {

                checkClassicStatus(sale_section_id)
            }
        )

        watch(
            () => form.lot_number.fields.requested_lotno.value,
            () => {
                if (form.lot.fields.lot_number.value !== null) return
                if (state.lotno_search_timeout) clearTimeout(state.lotno_search_timeout)
                state.lotno_search_timeout = setTimeout(() => {
                    checkLotNumberAvailability()
                }, 1000)
            }
        )

        onMounted(() => {
            checkClassicStatus(vehicle_asset.value?.sale_section_id || null)
        })

        onBeforeMount(() => {
            getSuggestedLotNos()
            setCustodyDate()
        })

        return {
            asset_description_modal,
            VEHICLE_STORE,
            form,
            state,
            vehicle_id,
            vehicle,
            vehicle_asset,
            vehicle_asset_id,
            saleSectionCodeUpdated,
            saveStep,
            sellerSelected,
            checkLotNumberAvailability,
            setLotNumber,
            resetLotnoState
        }
    }
}
</script>